import { Link, useNavigate } from 'react-router-dom'
import { Menu, Bell, ChevronDown, LogOut } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui'
import { useAuth } from '@/contexts/AuthContext'

interface ConsoleTopNavProps {
  onMenuClick?: () => void
}

export function ConsoleTopNav({ onMenuClick }: ConsoleTopNavProps) {
  const { developer, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // Get display name or fallback to email
  const displayName = developer?.display_name || developer?.email?.split('@')[0] || 'User'
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <header className="fixed top-0 left-0 right-0 z-40 h-14 border-b border-[var(--border-default)] bg-[var(--bg-base)]">
      <div className="flex h-full items-center justify-between px-4">
        {/* Left side - logo and mobile menu */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={onMenuClick}
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--accent)]">
              <span className="text-sm font-bold text-white">U</span>
            </div>
            <span className="text-lg font-semibold text-[var(--text-primary)]">
              Console
            </span>
          </Link>
        </div>

        {/* Right side - notifications and user menu */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <Button variant="ghost" size="sm">
            <Bell className="h-5 w-5" />
          </Button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2">
                <Avatar className="h-7 w-7">
                  {developer?.avatar_url && (
                    <AvatarImage src={developer.avatar_url} alt={displayName} />
                  )}
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden text-sm text-[var(--text-primary)] md:inline">
                  {displayName}
                </span>
                <ChevronDown className="h-4 w-4 text-[var(--text-muted)]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  {displayName}
                </p>
                <p className="text-xs text-[var(--text-muted)]">
                  {developer?.email}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-[var(--error)] cursor-pointer"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
